import { buildI18n } from "@repo/shared";
import type {
  FinancialSituationStatusDef,
  LocalizedString,
  MandateTemplateMembershipCollision,
  PlatformCondition,
  PlatformMembershipCollision,
  PlatformMembershipErrorResponse,
  PortfolioCollectionGoalType,
  PortfolioCollectionPlatform,
  ProductListItem,
} from "@repo/shared/types";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import { ApiError } from "@/lib/api/_client";
import {
  useCreatePlatform,
  useDeletePlatform,
  useReorderPlatforms,
  useUpdatePlatform,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { ProductWarningBadge } from "../products/product-warning-badge";
import { PlatformProductPicker } from "./platform-product-picker";
import { T } from "./platforms-table.i18n";

/** Editable condition row: statusKey (FS-model status) + selected values. */
type ConditionRow = { statusKey: string; values: string[] };

function toRows(conditions: PlatformCondition[] | null): ConditionRow[] {
  return (conditions ?? []).map((c) => ({ statusKey: c.statusKey, values: [...c.values] }));
}

function fromRows(rows: ConditionRow[]): PlatformCondition[] | null {
  const conditions = rows
    .map((row) => ({ statusKey: row.statusKey.trim(), values: row.values.filter(Boolean) }))
    .filter((c) => c.statusKey || c.values.length > 0);
  return conditions.length > 0 ? conditions : null;
}

type PlatformsTableProps = {
  portfolioCollectionId: string;
  platforms: PortfolioCollectionPlatform[];
  goalTypes: PortfolioCollectionGoalType[];
  /**
   * Derived statuses of the collection's linked financial situation model.
   * Null = the collection has no FS-model relation → the conditions editor is
   * hidden entirely (conditions require the relation).
   */
  statusDefs: FinancialSituationStatusDef[] | null;
  products: ProductListItem[];
  /** True while the products list is still fetching (it doesn't gate page paint). */
  productsLoading: boolean;
  /**
   * QL-198 — platform row ids with no active model portfolio tagged (from the
   * collection detail's platformStatus); flagged rows get a warning badge.
   */
  platformIdsMissingPortfolios?: string[];
};

export function PlatformsTable({
  portfolioCollectionId,
  platforms,
  goalTypes,
  statusDefs,
  products,
  productsLoading,
  platformIdsMissingPortfolios = [],
}: PlatformsTableProps) {
  const createPlatform = useCreatePlatform();
  const updatePlatform = useUpdatePlatform();
  const deletePlatform = useDeletePlatform();
  const reorderPlatforms = useReorderPlatforms();

  const isPending =
    createPlatform.isPending ||
    updatePlatform.isPending ||
    deletePlatform.isPending ||
    reorderPlatforms.isPending;

  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const getLabel = (i18n: LocalizedString) => localize(i18n);

  const goalTypeLabel = (goalTypeId: string) => {
    const gt = goalTypes.find((g) => g.id === goalTypeId);
    return gt ? getLabel(gt.labelI18n) : goalTypeId;
  };

  const statusLabel = (statusKey: string) => {
    const def = (statusDefs ?? []).find((d) => d.key === statusKey);
    return def ? getLabel(def.labelI18n) || def.key : statusKey;
  };

  const statusValues = (statusKey: string): string[] => {
    const def = (statusDefs ?? []).find((d) => d.key === statusKey);
    return (def?.thresholds ?? []).map((th) => th.value);
  };

  // Dialog state (shared for create + edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<PortfolioCollectionPlatform | null>(null);
  const [formLabel, setFormLabel] = useState("");
  const [formGoalTypes, setFormGoalTypes] = useState<string[]>([]);
  const [formConditions, setFormConditions] = useState<ConditionRow[]>([]);
  const [formProductIds, setFormProductIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [collisions, setCollisions] = useState<PlatformMembershipCollision[]>([]);
  // QL-375 — the template twin of the model-portfolio collisions.
  const [templateCollisions, setTemplateCollisions] = useState<
    MandateTemplateMembershipCollision[]
  >([]);

  const editingPlatformName = editingPlatform ? getLabel(editingPlatform.labelI18n) : "";

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const openCreate = () => {
    setEditingPlatform(null);
    setFormLabel("");
    setFormGoalTypes([]);
    setFormConditions([]);
    // A new platform starts with no products selected.
    setFormProductIds([]);
    setFormError(null);
    setCollisions([]);
    setTemplateCollisions([]);
    setDialogOpen(true);
  };

  const openEdit = (platform: PortfolioCollectionPlatform) => {
    setEditingPlatform(platform);
    setFormLabel(getLabel(platform.labelI18n));
    // Drop references to goal types that no longer exist (e.g. legacy key slugs
    // from before the id migration) so the save payload validates server-side.
    const knownIds = new Set(goalTypes.map((g) => g.id));
    setFormGoalTypes((platform.goalTypes ?? []).filter((id) => knownIds.has(id)));
    setFormConditions(toRows(platform.conditions));
    // Dropping stale product ids requires the loaded product list — filtering
    // against a still-loading (empty) list would wipe the platform's membership.
    const rawProductIds = platform.productIds ?? [];
    if (productsLoading) {
      setFormProductIds(rawProductIds);
    } else {
      const knownProductIds = new Set(products.map((p) => p.id));
      setFormProductIds(rawProductIds.filter((id) => knownProductIds.has(id)));
    }
    setFormError(null);
    setCollisions([]);
    setTemplateCollisions([]);
    setDialogOpen(true);
  };

  const toggleGoalType = (goalTypeId: string) => {
    setFormGoalTypes((prev) =>
      prev.includes(goalTypeId) ? prev.filter((id) => id !== goalTypeId) : [...prev, goalTypeId],
    );
  };

  const toggleConditionValue = (index: number, value: string) => {
    setFormConditions((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              values: row.values.includes(value)
                ? row.values.filter((v) => v !== value)
                : [...row.values, value],
            }
          : row,
      ),
    );
  };

  // Each status may appear in only one condition row: the engine ANDs rows and
  // a client has one value per status, so duplicate rows can never all match.
  const usedStatusKeys = new Set(formConditions.map((row) => row.statusKey).filter(Boolean));

  const handleSubmit = () => {
    const label = formLabel.trim();
    if (!label) {
      setFormError(t.labelRequired);
      return;
    }
    // A partially-filled condition row (status without values or vice versa) is a mistake.
    const hasIncompleteRow = formConditions.some((row) => {
      const hasKey = !!row.statusKey.trim();
      const hasValues = row.values.length > 0;
      return hasKey !== hasValues;
    });
    if (hasIncompleteRow) {
      setFormError(t.conditionRowIncomplete);
      return;
    }
    setFormError(null);
    setCollisions([]);
    setTemplateCollisions([]);

    const conditions = fromRows(formConditions);

    // A 422 with a `collisions` payload is the membership coverage rejection
    // (QP-226) — rendered as an explicit list, not a generic error line.
    const handleSaveError = (err: Error, fallback: string) => {
      if (err instanceof ApiError && err.status === 422) {
        const body = err.body as Partial<PlatformMembershipErrorResponse> | null | undefined;
        if (Array.isArray(body?.collisions) || Array.isArray(body?.templateCollisions)) {
          setCollisions(body?.collisions ?? []);
          setTemplateCollisions(body?.templateCollisions ?? []);
          return;
        }
      }
      setFormError(err.message || fallback);
    };

    if (editingPlatform) {
      // Merge existing i18n with the updated value for the current language.
      const labelI18n: LocalizedString = { ...editingPlatform.labelI18n, [userLanguage]: label };
      updatePlatform.mutate(
        {
          collectionId: portfolioCollectionId,
          platformId: editingPlatform.id,
          data: {
            labelI18n,
            goalTypes: formGoalTypes,
            conditions,
            productIds: formProductIds,
          },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) => handleSaveError(err, t.failedToUpdate),
        },
      );
    } else {
      createPlatform.mutate(
        {
          collectionId: portfolioCollectionId,
          data: {
            labelI18n: buildI18n(label, userLanguage, tenantSupportedLanguages),
            goalTypes: formGoalTypes,
            conditions,
            productIds: formProductIds,
          },
        },
        {
          onSuccess: () => setDialogOpen(false),
          onError: (err: Error) => handleSaveError(err, t.failedToAdd),
        },
      );
    }
  };

  const handleTranslationSave = (
    platform: PortfolioCollectionPlatform,
    labelI18n: LocalizedString,
  ) => {
    updatePlatform.mutate(
      {
        collectionId: portfolioCollectionId,
        platformId: platform.id,
        data: {
          labelI18n,
          goalTypes: platform.goalTypes ?? [],
          conditions: platform.conditions,
        },
      },
      {
        onError: (err: Error) => showAlert(t.error, err.message || t.failedToUpdate, "destructive"),
      },
    );
  };

  const handleDelete = () => {
    if (!platformToDelete) return;
    deletePlatform.mutate(
      { collectionId: portfolioCollectionId, platformId: platformToDelete },
      {
        onSuccess: () => setPlatformToDelete(null),
        onError: (err: Error) => {
          showAlert(t.error, err.message || t.failedToDelete, "destructive");
          setPlatformToDelete(null);
        },
      },
    );
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= platforms.length) return;
    const orderedIds = platforms.map((p) => p.id);
    const [moved] = orderedIds.splice(index, 1);
    orderedIds.splice(target, 0, moved!);
    reorderPlatforms.mutate(
      { collectionId: portfolioCollectionId, orderedIds },
      {
        onError: (err: Error) =>
          showAlert(t.error, err.message || t.failedToReorder, "destructive"),
      },
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={openCreate} disabled={isPending}>
            {t.addPlatform}
          </Button>
        </div>

        {platforms.length === 0 ? (
          <EmptyState>{t.noPlatforms}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t.order}</TableHead>
                <TableHead>{t.label}</TableHead>
                <TableHead>{t.goalTypes}</TableHead>
                <TableHead>{t.conditions}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {platforms.map((platform, index) => (
                <TableRow key={platform.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <Button
                        size="icon"
                        tooltip={t.moveUp}
                        variant="ghost"
                        onClick={() => handleMove(index, -1)}
                        disabled={isPending || index === 0}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.moveDown}
                        variant="ghost"
                        onClick={() => handleMove(index, 1)}
                        disabled={isPending || index === platforms.length - 1}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{getLabel(platform.labelI18n)}</span>
                      {/* QL-198 — no active model portfolio tagged to this platform */}
                      {platformIdsMissingPortfolios.includes(platform.id) && (
                        <ProductWarningBadge warnings={[t.missingPortfoliosWarning]} />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(platform.goalTypes ?? []).length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">{t.anyGoalType}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {platform.goalTypes.map((goalTypeId) => (
                          <Badge key={goalTypeId} variant="outline" className="text-xs">
                            {goalTypeLabel(goalTypeId)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {!platform.conditions || platform.conditions.length === 0 ? (
                      <span className="text-xs text-muted-foreground italic">{t.anyClient}</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {platform.conditions.map((condition) => (
                          <span key={condition.statusKey} className="text-xs">
                            {statusLabel(condition.statusKey)} = {condition.values.join(" | ")}
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.editPlatform}
                        variant="ghost"
                        onClick={() => openEdit(platform)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TranslationButton
                        value={platform.labelI18n}
                        onSave={(labelI18n) => handleTranslationSave(platform, labelI18n)}
                        supportedLanguages={tenantSupportedLanguages}
                        disabled={isPending}
                      />
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setPlatformToDelete(platform.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          // A reopened dialog must not show stale collision results.
          if (!open) {
            setCollisions([]);
            setTemplateCollisions([]);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPlatform
                ? editingPlatformName
                  ? `${t.editPlatform} (${editingPlatformName})`
                  : t.editPlatform
                : t.addPlatform}
            </DialogTitle>
            <DialogDescription>{t.createDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="platform-label">{t.label}</Label>
              <Input
                id="platform-label"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
                placeholder={t.labelPlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label>{t.goalTypes}</Label>
              {goalTypes.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t.noGoalTypesConfigured}</p>
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    {goalTypes.map((gt) => (
                      <div key={gt.id} className="flex items-center gap-2 text-sm font-normal">
                        <Checkbox
                          id={`platform-goal-type-${gt.id}`}
                          checked={formGoalTypes.includes(gt.id)}
                          onCheckedChange={() => toggleGoalType(gt.id)}
                          disabled={isPending}
                        />
                        <Label
                          htmlFor={`platform-goal-type-${gt.id}`}
                          className="font-normal cursor-pointer"
                        >
                          {getLabel(gt.labelI18n)}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">{t.goalTypesHelp}</p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t.products}</Label>
              <PlatformProductPicker
                products={products}
                selectedIds={formProductIds}
                onChange={setFormProductIds}
                disabled={isPending}
                labels={{
                  selectAll: t.selectAllProducts,
                  noProducts: t.noProductsConfigured,
                  help: t.productsHelp,
                }}
                getLabel={getLabel}
              />
            </div>

            {/* Conditions require the collection's FS-model relation (set on the
                Settings tab) — without it the section is hidden entirely. */}
            {statusDefs !== null && (
              <div className="space-y-2">
                <Label>{t.conditionsLabel}</Label>
                {formConditions.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t.noConditions}</p>
                )}
                {formConditions.map((row, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional editors
                  <div key={index} className="rounded-md border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Select
                        value={row.statusKey || undefined}
                        onValueChange={(statusKey) =>
                          setFormConditions((prev) =>
                            prev.map((r, i) =>
                              // Changing the status resets the picked values.
                              i === index ? { statusKey, values: [] } : r,
                            ),
                          )
                        }
                        disabled={isPending}
                      >
                        <SelectTrigger className="w-56" aria-label={t.statusPlaceholder}>
                          <SelectValue placeholder={t.statusPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {(statusDefs ?? []).map((def) => (
                            <SelectItem
                              key={def.key}
                              value={def.key}
                              disabled={def.key !== row.statusKey && usedStatusKeys.has(def.key)}
                            >
                              {getLabel(def.labelI18n) || def.key}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex-1" />
                      <Button
                        size="icon"
                        tooltip={t.removeCondition}
                        variant="ghost"
                        onClick={() =>
                          setFormConditions((prev) => prev.filter((_, i) => i !== index))
                        }
                        disabled={isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {row.statusKey && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">{t.valuesLabel}</p>
                        <div className="flex flex-wrap gap-3">
                          {statusValues(row.statusKey).map((value) => (
                            <div key={value} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                id={`condition-${index}-${value}`}
                                checked={row.values.includes(value)}
                                onCheckedChange={() => toggleConditionValue(index, value)}
                                disabled={isPending}
                              />
                              <Label
                                htmlFor={`condition-${index}-${value}`}
                                className="font-normal cursor-pointer capitalize"
                              >
                                {value}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setFormConditions((prev) => [...prev, { statusKey: "", values: [] }])
                  }
                  disabled={isPending || formConditions.length >= (statusDefs?.length ?? 0)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t.addCondition}
                </Button>
                <p className="text-xs text-muted-foreground">{t.conditionsHelp}</p>
              </div>
            )}

            {collisions.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3 space-y-2">
                <p className="text-sm text-destructive font-medium">{t.collisionTitle}</p>
                <div className="max-h-48 overflow-auto">
                  <Table variant="data">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.collisionProductColumn}</TableHead>
                        <TableHead>{t.collisionPortfolioColumn}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {collisions.map((collision) => (
                        <TableRow key={`${collision.productId}-${collision.portfolioId}`}>
                          <TableCell className="text-xs">
                            {getLabel(collision.productNameI18n)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {collision.portfolioKind === "dynamic"
                              ? t.collisionDynamicPortfolio(String(collision.riskScore ?? ""))
                              : getLabel(collision.portfolioNameI18n)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground">{t.collisionHint}</p>
              </div>
            )}

            {templateCollisions.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3 space-y-2">
                <p className="text-sm text-destructive font-medium">{t.templateCollisionTitle}</p>
                <div className="max-h-48 overflow-auto">
                  <Table variant="data">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t.collisionProductColumn}</TableHead>
                        <TableHead>{t.templateCollisionTemplateColumn}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templateCollisions.map((collision) => (
                        <TableRow key={`${collision.productId}-${collision.templateId}`}>
                          <TableCell className="text-xs">
                            {getLabel(collision.productNameI18n)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {getLabel(collision.templateNameI18n)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground">{t.templateCollisionHint}</p>
              </div>
            )}

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button onClick={handleSubmit} loading={isPending}>
              {editingPlatform ? t.save : t.addPlatform}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deletePlatform}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
